import styled from 'styled-components';
import { ContainerBgWhite } from 'styles/containerBgWhite';
import { BonusFilters } from './BonusFilters';
import { BonusList } from './BonusList';
import { BonusPayload, IBonusFilters, IBonusList, Reason } from 'types';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import payrollApi from 'api/payrollApi';
import CustomButton from 'styles/buttonStyled';
import {
  ALLOWANCE_SORT_FIELDS,
  ContractSalaryConfirmStatus,
  MONTH_YEAR_FORMAT,
  SORT_TYPES,
} from 'constants/payroll';
import { ExportOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { Modal, TablePaginationConfig } from 'antd';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { Notification } from 'constants/notification';
import { getErrorMessage } from 'utils/getErrorMessage';
import { notificationToast } from 'components/notificationToast';
import { ReasonForm } from 'components/Forms';
import { selectEmployee } from 'features/employee/employeeSlice';
import { exportUsersHaveBonus } from 'features/employee/employeeAction';

interface BonusProps {
  monthYear: string;
  fetchListStatusTabPayrollAPI: () => void;
}

export function Bonus({ monthYear, fetchListStatusTabPayrollAPI }: BonusProps) {
  const { t } = useTranslation(['action', 'payroll']);
  const selectRowKey = useAppSelector((state) => state.employee.selectRowKey) as string[];
  let dispatch = useAppDispatch();
  const [bonusList, setBonusList] = useState<Array<IBonusList>>([]);
  const [totalResult, setTotalResult] = useState(0);
  const [statusConfirm, setStatusConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConfirm, setIsLoadingConfirm] = useState(false);
  const [offset, setOffset] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState<Partial<IBonusFilters>>({
    search: '',
    line: '',
  });
  const isCheckAll: boolean = useAppSelector(selectEmployee).isCheckAll;

  const filtersMemo = useMemo(() => {
    if (isCheckAll) {
      return {
        ...filters,
        monthYear: monthYear ?? moment(new Date()).format('YYYY-MM'),
      };
    }
    return {
      ...filters,
      monthYear: monthYear ?? moment(new Date()).format('YYYY-MM'),
      offset,
      limit,
    };
  }, [filters, monthYear, offset, limit, isCheckAll]);

  const bonusPayload: Partial<BonusPayload> = {
    search: filters.search || '',
  };

  const fetchBonusListAPI = async () => {
    try {
      setIsLoading(true);
      const response = await payrollApi.getAllUserHaveBonus(filtersMemo);

      setBonusList(
        response.data?.result.map((item: IBonusList) => ({ ...item, userId: item?.user?.id })),
      );
      setTotalResult(response.data?.totalResult || 0);
      setStatusConfirm(response.data?.status || '');

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBonusListAPI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersMemo]);

  const handleFiltersChange = ({ ...payload }: BonusPayload) => {
    setOffset(1);

    setFilters((prevFilter) => ({
      ...prevFilter,
      ...payload,
    }));
  };

  const handleExport = () => {
    if (selectRowKey.length === 0) {
      return Modal.warning({
        title: t('modal:none_select'),
      });
    }

    const userArray = bonusList
      ?.filter((item: any) =>
        selectRowKey?.some(
          (i: any) => i === item?.id || i === item?.userId || i === item?.user?.id,
        ),
      )
      ?.map((item: any) => item?.user?.id || item?.userId || item?.id);

    const userIds = Array.from(new Set(userArray));
    const bodyExport = {
      monthYear: moment(filtersMemo.monthYear).format('YYYY-MM'),
      userIds,
    };

    dispatch(
      exportUsersHaveBonus({
        ...bodyExport,
      }),
    );
  };

  const handleConfirm = async () => {
    try {
      const payload = {
        monthYear: moment(filtersMemo.monthYear).format(MONTH_YEAR_FORMAT),
        status: ContractSalaryConfirmStatus.APPROVED,
      };
      setIsLoadingConfirm(true);
      const response = await payrollApi.confirmBonus(payload);
      setIsLoadingConfirm(false);
      notificationToast(Notification.Type.Success, response.message, Notification.Duration._3s);
      fetchListStatusTabPayrollAPI();
      fetchBonusListAPI();
    } catch (error) {
      setIsLoadingConfirm(false);
      const message = getErrorMessage(error);
      notificationToast(Notification.Type.Error, message, Notification.Duration._3s);
    }
  };

  const handleConfirmBonus = () => {
    Modal.confirm({
      title: <div style={{ fontWeight: 'bold' }}>{t('payroll:confirm_bonus_title')}</div>,
      okButtonProps: { loading: isLoadingConfirm },
      okText: t('action:ok'),
      cancelText: t('action:cancel'),
      onOk: handleConfirm,
    });
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<any>,
  ) => {
    if (pagination?.current === offset) {
      setOffset(1);

      setFilters((prevFilters) => ({
        ...prevFilters,
        viewBy: sorter.column
          ? ALLOWANCE_SORT_FIELDS[sorter.field as keyof typeof ALLOWANCE_SORT_FIELDS]
          : ALLOWANCE_SORT_FIELDS.fullName,
        sortBy: sorter.column
          ? SORT_TYPES[sorter.order as keyof typeof SORT_TYPES]
          : SORT_TYPES.ascend,
      }));
    }
  };

  const handleReset = async (reason: string) => {
    try {
      const params = {
        monthYear: moment(filtersMemo.monthYear).format(MONTH_YEAR_FORMAT),
        timesheetType: 'bonus',
        reason,
      };

      setIsLoadingConfirm(true);
      const response = await payrollApi.resetConfirmPayroll(params);
      setIsLoadingConfirm(false);
      notificationToast(Notification.Type.Success, response.message, Notification.Duration._3s);
      fetchListStatusTabPayrollAPI();
      fetchBonusListAPI();
    } catch (error) {
      setIsLoadingConfirm(false);
      const message = getErrorMessage(error);
      notificationToast(Notification.Type.Error, message, Notification.Duration._3s);
    }
  };

  const handleResetBonus = () => {
    let reason = '';
    Modal.confirm({
      title: <div style={{ fontWeight: 'bold' }}>{t('reset_reason')}</div>,
      content: (
        <>
          <ReasonForm
            onSubmit={(payload: Reason) => {
              if (!payload.reason) return;
              reason = payload.reason;
            }}
          />
          <p style={{ margin: 0, color: 'red', fontStyle: 'italic' }}>
            {t('payroll:payroll_deleted', { monthYear })}
          </p>
        </>
      ),
      okButtonProps: { loading: isLoading },
      okText: t('action:ok'),
      cancelText: t('action:cancel'),
      onOk: () => handleReset(reason),
      onCancel: () => (reason = ''),
    });
  };

  return (
    <ContainerBgWhite>
      <StyledWrapperFilters>
        <BonusFilters initialValues={bonusPayload} onSubmit={handleFiltersChange}></BonusFilters>

        <StyledWrapperActionBtn>
          {statusConfirm === ContractSalaryConfirmStatus.APPROVED ? (
            <>
              <CustomButton loading={false} onClick={handleResetBonus}>
                {t('reset')}
              </CustomButton>
              <ButtonConfirmed>{t('Confirmed')}</ButtonConfirmed>
            </>
          ) : (
            <CustomButton loading={false} onClick={handleConfirmBonus}>
              {t('Confirm')}
            </CustomButton>
          )}

          <CustomButton
            icon={<ExportOutlined />}
            onClick={handleExport}
            disabled={selectRowKey.length === 0}
          >
            {t('export')}
          </CustomButton>
        </StyledWrapperActionBtn>
      </StyledWrapperFilters>
      <BonusList
        loading={isLoading}
        data={bonusList?.filter((item: any) => !item?.deleteFlag) || []}
        totalResult={totalResult}
        offset={offset}
        limit={limit}
        setOffset={setOffset}
        setLimit={setLimit}
        onRefresh={fetchBonusListAPI}
        onTableChange={handleTableChange}
        monthYear={filtersMemo.monthYear ?? ''}
      />
    </ContainerBgWhite>
  );
}
const StyledWrapperFilters = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;
const StyledWrapperActionBtn = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;
const ButtonConfirmed = styled.div`
  background-color: var(--cl_success100);
  color: var(--cl_success600);
  font-weight: bold;
  border-radius: 5px;
  text-align: center;
  display: inline;
  padding: 5px 10px;
  box-shadow: rgba(0, 0, 0, 0.2) 0px 0px 4px;
`;
